import PropTypes from 'prop-types';
import React from 'react';
import CompanyPageLayout from '../../../components/CompanyPages/CompanyPageLayout/CompanyPageLayout';
import IndustryTable from '../../../components/Employer/Benefits/IndustryTable/IndustryTable';
import RowRecord from '../../../components/Employer/Benefits/RowRecord/RowRecord';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
    PAGE_TABS,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import { JobContextProvider } from '../../../contexts/JobContext';

import './EmployerWorkLife.scss';

export default class EmployerWorkLife extends React.PureComponent {
    // Main Content Componnets
    // ==============================================================================
    renderIntroDescriptionCard = (company) => {
        return (
            <div className="intro-description-card">
                <h3 className="page-title">Work-Life Balance</h3>
                <p className="declaration">{`Women employees at ${company.companyName} on their work-life balance, and whether ${company.companyName}  offers remote-jobs, part-time jobs or is a flexible place to work.`}</p>
            </div>
        );
    };

    renderHowFlexibleCard = (company, workLifeData) => {
        const companyAverage =
            workLifeData &&
            workLifeData.rating &&
            workLifeData.rating.company ? (
                `${Math.round(workLifeData.rating.company)}`
            ) : (
                <a href="/company-reviews">Leave A Review</a>
            );
        const industryAverage =
            workLifeData && workLifeData.rating && workLifeData.rating.industry
                ? `${Math.round(workLifeData.rating.industry)}`
                : 'Not Available';
        const theads = ['Company Average', 'Industry Average'];
        const rowRecords = [
            [{ data: companyAverage }, { data: industryAverage }],
        ];
        return (
            <section className="how-flexible-card">
                <div className="card-title-styling blockHeader-styling">{`How Flexible Do Employees Think ${company.companyName} Is? (on a scale of 1-5)`}</div>
                <div className="employer-work-life-card-body">
                    {this.renderResponsiveIndustrialRecords(theads, rowRecords)}
                </div>
            </section>
        );
    };

    renderWorkLifeSupportiveCard = (company, workLifeData) => {
        const theadRecordArray = [
            'Work-Life Friendly Attributes',
            'Company Average',
            'Industry Average',
        ];
        const rowRecordArray = this.constructIndustryTableRowRecords(
            company,
            workLifeData
        );
        return (
            <section className="work-life-supportive-card">
                <div className="card-title-styling blockHeader-styling">{`How Work-Life Supportive Is ${company.companyName}`}</div>
                <div className="employer-work-life-card-body">
                    {this.renderResponsiveIndustrialRecords(
                        theadRecordArray,
                        rowRecordArray
                    )}
                </div>
            </section>
        );
    };

    renderFlexibilityData = (company, flexibilityData) => {
        return (
            <section className="flexibility-data-card">
                <div className="card-title-styling blockHeader-styling">{`Does ${company.companyName} Offer Flexible, Remote and Part-Time Jobs`}</div>
                <div className="employer-work-life-card-body">
                    {flexibilityData.map((record) => {
                        return (
                            <RowRecord
                                key={record.type}
                                {...record}
                                onCommentSubmit={() => {}}
                            />
                        );
                    })}
                </div>
            </section>
        );
    };

    // SUPPORTING FUNCTIONS
    // ===================================================================
    renderResponsiveIndustrialRecords = (theads, rowRecords) => {
        return (
            <IndustryTable
                theads={theads}
                rowRecords={rowRecords}
                bodyTHAlign="center"
            />
        );
    };

    constructIndustryTableRowRecords = (company, workLifeData) => {
        const hasAggregatedData = !!(
            workLifeData && workLifeData.aggregatedData
        );
        const HoursRow =
            hasAggregatedData && workLifeData.aggregatedData.hours
                ? this.generateSingleRowArray(
                      workLifeData.aggregatedData.hours,
                      `Hours: % of women who say ${company.companyName} has work-life friendly hours.`
                  )
                : this.generateSingleRowArray(
                      {},
                      `Hours: % of women who say ${company.companyName} has work-life friendly hours.`
                  );
        const PoliciesRow =
            hasAggregatedData && workLifeData.aggregatedData.policies
                ? this.generateSingleRowArray(
                      workLifeData.aggregatedData.policies,
                      `Policies: % of women who say ${company.companyName} has work-life friendly policies.`
                  )
                : this.generateSingleRowArray(
                      {},
                      `Policies: % of women who say ${company.companyName} has work-life friendly policies.`
                  );
        const CulutreRow =
            hasAggregatedData && workLifeData.aggregatedData.culture
                ? this.generateSingleRowArray(
                      workLifeData.aggregatedData.culture,
                      `Culture: % of women who say ${company.companyName} has work-life friendly culture.`
                  )
                : this.generateSingleRowArray(
                      {},
                      `Culture: % of women who say ${company.companyName} has work-life friendly culture.`
                  );
        const WorkLifeBalanceRow =
            hasAggregatedData && workLifeData.aggregatedData.work_life
                ? this.generateSingleRowArray(
                      workLifeData.aggregatedData.work_life,
                      `Work-Life Balance: % of women who say better work life balance would help retain them.`
                  )
                : this.generateSingleRowArray(
                      {},
                      `Work-Life Balance: % of women who say better work life balance would help retain them.`
                  );

        const Array = [
            HoursRow && HoursRow.length ? HoursRow : null,
            PoliciesRow && PoliciesRow.length ? PoliciesRow : null,
            CulutreRow && CulutreRow.length ? CulutreRow : null,
            WorkLifeBalanceRow && WorkLifeBalanceRow.length
                ? WorkLifeBalanceRow
                : null,
        ];

        return Array.filter(Boolean);
    };

    generateSingleRowArray = (object, firstTitle) => {
        const secondData =
            object.companyData && !Number.isNaN(object.companyData)
                ? `${this.calculatePercentage(object.companyData)}%`
                : 'Not Available';
        const thirdData =
            object.industryData && !Number.isNaN(object.industryData)
                ? `${this.calculatePercentage(object.industryData)}%`
                : 'Not Available';
        return [
            { data: firstTitle },
            { data: secondData },
            { data: thirdData },
        ];
    };

    calculatePercentage = (number) => {
        return (number * 100).toFixed(2);
    };

    render() {
        const {
            company,
            workLifeData,
            flexibilityData,
            userJobProfile,
        } = this.props;
        return (
            <div className="page-Employer-EmployerWorkLife">
                <SnowplowPageViewTracker
                    pageCompanyId={company.companyId}
                    pageEntityId={company.companyId}
                    pageSection={PAGE_VIEW_SECTIONS.COMPANY_PROFILE}
                    pageTab={PAGE_TABS.COMPANY_WORK_LIFE_BALANCE}
                    pageType={PAGE_VIEW_TYPES.CONTENT}
                />
                <JobContextProvider
                    content={[]}
                    userJobProfile={userJobProfile}
                >
                    <CompanyPageLayout company={company}>
                        <section className="main-section">
                            <header>
                                {this.renderIntroDescriptionCard(company)}
                            </header>
                            <div className="content-controlled-width">
                                <div className="content-box">
                                    {this.renderHowFlexibleCard(
                                        company,
                                        workLifeData
                                    )}
                                </div>
                                <div className="content-box">
                                    {this.renderWorkLifeSupportiveCard(
                                        company,
                                        workLifeData
                                    )}
                                </div>
                                <div className="content-box">
                                    {this.renderFlexibilityData(
                                        company,
                                        flexibilityData
                                    )}
                                </div>
                            </div>
                        </section>
                    </CompanyPageLayout>
                </JobContextProvider>
            </div>
        );
    }
}

EmployerWorkLife.propTypes = {
    company: PropTypes.shape({
        companyName: PropTypes.string,
        companyId: PropTypes.number,
        logo: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        companyUrl: PropTypes.string,
        remoteUrl: PropTypes.string,
        careerUrl: PropTypes.string,
        rating: PropTypes.number,
        isSponsor: PropTypes.bool,
        reviewCount: PropTypes.number,
        isFollowed: PropTypes.bool,
        employerInfo: PropTypes.shape({
            banner: PropTypes.shape({
                src: PropTypes.string,
                alt: PropTypes.string,
            }),
            hq: PropTypes.shape({
                city: PropTypes.string,
                state: PropTypes.string,
                country: PropTypes.string,
            }),
            jobCount: PropTypes.number,
            companySize: PropTypes.string,
        }),
        industry: PropTypes.string,
        googleSeo: PropTypes.string,
        bannerText: PropTypes.string,
        version: PropTypes.number,
    }).isRequired,
    industry: PropTypes.string,
    workLifeData: PropTypes.shape({
        rating: PropTypes.shape({
            company: PropTypes.number,
            industry: PropTypes.number,
        }),
        aggregatedData: PropTypes.shape({
            culture: PropTypes.shape({
                companyData: PropTypes.number,
                industryData: PropTypes.number,
            }),
            policies: PropTypes.shape({
                companyData: PropTypes.number,
                industryData: PropTypes.number,
            }),
            hours: PropTypes.shape({
                companyData: PropTypes.number,
                industryData: PropTypes.number,
            }),
            work_life: PropTypes.shape({
                companyData: PropTypes.number,
                industryData: PropTypes.number,
            }),
        }),
        flexibilityComments: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    flexibilityData: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            fieldName: PropTypes.string,
            hight: PropTypes.string,
            low: PropTypes.string,
            median: PropTypes.string,
            tipInputType: PropTypes.string,
            type: PropTypes.string,
        })
    ).isRequired,
    userJobProfile: PropTypes.shape({
        hash: PropTypes.string,
        isCompleted: PropTypes.bool,
    }),
};

EmployerWorkLife.defaultProps = {
    industry: null,
    userJobProfile: {},
};
